import React from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { Flex, TextV2 } from '@withjoy/joykit';
import { NavLink } from '@react-router';
import { isSupportedEventPage } from '../../layouts/layout.utils';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { label4ToEventTitleOverride, label6ToNavigationOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useColorPaletteProvider } from '../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import { pxToRem } from '@withjoy/joykit/theme';
import { useGuestSiteNavUtm } from '../../common/utils/useGuestSiteNavUtm';
import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import { applyUtmParametersToUrl } from '@shared/utils/utmParameters';
import { useMcpPreviewForceSsr } from '@shared/context/mcpPreviewForceSsr';

type FooterNavProps = Readonly<{
  pages: ReadonlyArray<EventPageFragment>;
  eventHandle: string;
  eventDisplayName: Maybe<string>;
  handleMenuClicked?: (item: string, utm?: WritableUtmParameters) => void;
}>;

const FooterNav: React.FC<FooterNavProps> = ({ pages, eventHandle, eventDisplayName, handleMenuClicked }) => {
  const eventPageRoute = useEventPageRoute(eventHandle, pages);
  const resolveNavUtm = useGuestSiteNavUtm('footer_nav');
  const { isDefaultPage } = useDefaultPage(pages);

  const { accentColor, pageFontColor } = useColorPaletteProvider();
  // MCP preview: omit Moments/RSVP (mobile-only nav, no hover for a tooltip) — see MCP_PREVIEW.md#charm-flow-suppression.
  const isMcpPreview = useMcpPreviewForceSsr();

  return (
    <Flex
      width="100%"
      flexDirection="column"
      rowGap={4}
      paddingX={7}
      paddingTop={pxToRem(96)}
      paddingBottom={8}
    >
      {eventDisplayName && (
        <GuestSiteTypographyOverride override={label4ToEventTitleOverride}>
          <TextV2
            typographyVariant="label4"
            fontWeight={500}
            textTransform="none"
            color={pageFontColor || 'mono8'}
          >
            {eventDisplayName}
          </TextV2>
        </GuestSiteTypographyOverride>
      )}
      <GuestSiteTypographyOverride override={label6ToNavigationOverride}>
        {pages.map(page => {
          if (!isSupportedEventPage(page.type) || !page.pageTitle) {
            return null;
          }
          if (isMcpPreview && (page.pageSlug === 'moments' || page.pageSlug === 'rsvp')) {
            return null;
          }

          const navUtm = resolveNavUtm(page.pageSlug);

          return (
            <NavLink
              key={page.id}
              to={applyUtmParametersToUrl(eventPageRoute.goToPath(page.type, page.pageSlug), navUtm)}
              onClick={() => handleMenuClicked?.(page.pageSlug, navUtm)}
              exact={isDefaultPage(page.pageSlug, page.type)}
              style={{ textDecoration: 'none' }}
            >
              <TextV2
                typographyVariant="label6"
                fontSize="19px"
                color={accentColor || 'mono15'}
              >
                {page.pageTitle}
              </TextV2>
            </NavLink>
          );
        })}
      </GuestSiteTypographyOverride>
    </Flex>
  );
};

export default FooterNav;
